import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getTranslation, translateCategory } from '../translations';

const BlogsList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const navigate = useNavigate();
  // Blog sayfası her zaman Türkçe olacak
  const language = 'tr';

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      setError('');
      try {
        // First get all published blogs, then sort client-side
        // This handles cases where published_at might be null
        let q = query(
          collection(db, 'blogs'),
          where('status', '==', 'published')
        );

        const querySnapshot = await getDocs(q);
        const blogsData = [];
        querySnapshot.forEach((doc) => {
          blogsData.push({ id: doc.id, ...doc.data() });
        });
        // Sort by published_at descending, handling null values
        blogsData.sort((a, b) => {
          const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
          const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
          return dateB - dateA;
        });
        setBlogs(blogsData);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError(getTranslation(language, 'errorLoadingBlogs') || 'Error loading blogs');
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [language]);

  const categories = [...new Set(blogs.map(blog => blog.category).filter(Boolean))];
  const filteredBlogs = selectedCategory
    ? blogs.filter(blog => blog.category === selectedCategory)
    : blogs;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{ fontSize: 18, color: '#666' }}>
          {getTranslation(language, 'loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{ fontSize: 18, color: '#d32f2f' }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '80px 20px 40px'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
        padding: '0 20px',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: 48
        }}>
          <h1 style={{
            fontSize: 48,
            fontWeight: 700,
            color: '#1a1a1a',
            marginBottom: 16,
            letterSpacing: '-0.02em'
          }}>
            {getTranslation(language, 'blogs') || 'Blogs'}
          </h1>
          <p style={{
            fontSize: 18,
            color: '#666',
            maxWidth: 600,
            margin: '0 auto'
          }}>
            {getTranslation(language, 'blogsDescription') || 'Discover fitness tips, workout plans, and health insights'}
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 32,
            justifyContent: 'center'
          }}>
            <button
              onClick={() => setSelectedCategory('')}
              style={{
                padding: '10px 20px',
                borderRadius: 24,
                border: '2px solid',
                borderColor: selectedCategory === '' ? '#1a1a1a' : '#ddd',
                background: selectedCategory === '' ? '#1a1a1a' : '#fff',
                color: selectedCategory === '' ? '#fff' : '#666',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {getTranslation(language, 'allCategories') || 'All Categories'}
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 24,
                  border: '2px solid',
                  borderColor: selectedCategory === category ? '#1a1a1a' : '#ddd',
                  background: selectedCategory === category ? '#1a1a1a' : '#fff',
                  color: selectedCategory === category ? '#fff' : '#666',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'capitalize'
                }}
              >
                {translateCategory(category, language)}
              </button>
            ))}
          </div>
        )}

        {/* Blog Grid */}
        {filteredBlogs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 60,
            color: '#666',
            fontSize: 18
          }}>
            {getTranslation(language, 'noBlogsFound') || 'No blogs found'}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 32
          }}>
            {filteredBlogs.map(blog => (
              <div
                key={blog.id}
                onClick={() => navigate(`/blogs/${blog.slug}`)}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Cover Image */}
                {blog.cover_image && (
                  <div style={{
                    width: '100%',
                    height: 200,
                    backgroundImage: `url(${blog.cover_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }} />
                )}
                
                {/* Content */}
                <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Category */}
                  {blog.category && (
                    <div style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 12
                    }}>
                      {translateCategory(blog.category, language)}
                    </div>
                  )}

                  {/* Title */}
                  <h2 style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    marginBottom: 12,
                    lineHeight: 1.3,
                    flex: 1
                  }}>
                    {blog.title}
                  </h2>

                  {/* Description */}
                  {blog.description && (
                    <p style={{
                      fontSize: 14,
                      color: '#666',
                      lineHeight: 1.6,
                      marginBottom: 16,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {blog.description}
                    </p>
                  )}

                  {/* Meta Info */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 'auto',
                    paddingTop: 16,
                    borderTop: '1px solid #eee'
                  }}>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      {blog.reading_time && (
                        <span>{blog.reading_time} {getTranslation(language, 'minRead') || 'min read'}</span>
                      )}
                    </div>
                    {blog.published_at && (
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {formatDate(blog.published_at)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogsList;
